import { useState } from "react";
import { StickyNote, Plus, Loader2, Pencil, Trash2, X, Check } from "lucide-react";
import { format, parseISO } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useVendorNotes } from "@/hooks/useVendorNotes";
import type { VendorNote } from "@/types/negotiations";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface VendorNotesPanelProps {
  /** Vendor name — passed to useVendorNotes. */
  vendorName: string;
}

// ---------------------------------------------------------------------------
// NoteRow — individual note with inline edit + delete confirm
// ---------------------------------------------------------------------------

interface NoteRowProps {
  note: VendorNote;
  onUpdate: (id: string, body: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  isMutating: boolean;
}

function NoteRow({ note, onUpdate, onDelete, isMutating }: NoteRowProps) {
  const [editing, setEditing] = useState(false);
  const [editBody, setEditBody] = useState(note.body);
  const [confirmDelete, setConfirmDelete] = useState(false);

  async function handleSave() {
    if (!editBody.trim() || editBody.trim() === note.body) {
      setEditing(false);
      setEditBody(note.body);
      return;
    }
    await onUpdate(note.id, editBody.trim());
    setEditing(false);
  }

  function handleCancelEdit() {
    setEditing(false);
    setEditBody(note.body);
  }

  function handleStartEdit() {
    setEditBody(note.body);
    setEditing(true);
  }

  async function handleConfirmDelete() {
    await onDelete(note.id);
    setConfirmDelete(false);
  }

  return (
    <li className="flex gap-3 border-l-2 border-border/60 pl-3 group">
      <div className="flex-1 min-w-0">
        {editing ? (
          <div className="space-y-1.5">
            <textarea
              className="w-full min-h-[60px] rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-y"
              value={editBody}
              onChange={(e) => setEditBody(e.target.value)}
              autoFocus
              disabled={isMutating}
            />
            <div className="flex gap-1.5">
              <Button
                size="sm"
                className="h-7 px-2.5 text-xs gap-1"
                onClick={handleSave}
                disabled={isMutating || !editBody.trim()}
              >
                {isMutating ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <Check className="w-3 h-3" />
                )}
                Save
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2.5 text-xs gap-1"
                onClick={handleCancelEdit}
                disabled={isMutating}
              >
                <X className="w-3 h-3" />
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <>
            <p className="text-sm text-foreground whitespace-pre-wrap">{note.body}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {note.created_at ? format(parseISO(note.created_at), "MMM d, yyyy 'at' h:mm a") : "—"}
            </p>
          </>
        )}
      </div>

      {!editing && (
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
          <Button
            size="sm"
            variant="ghost"
            className="h-7 w-7 p-0"
            title="Edit note"
            onClick={handleStartEdit}
            disabled={isMutating}
          >
            <Pencil className="w-3.5 h-3.5" />
            <span className="sr-only">Edit note</span>
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 w-7 p-0 text-destructive hover:text-destructive"
            title="Delete note"
            onClick={() => setConfirmDelete(true)}
            disabled={isMutating}
          >
            <Trash2 className="w-3.5 h-3.5" />
            <span className="sr-only">Delete note</span>
          </Button>
        </div>
      )}

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete note?</AlertDialogTitle>
            <AlertDialogDescription>
              This note will be permanently deleted and cannot be recovered.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isMutating}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={isMutating}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isMutating ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </li>
  );
}

// ---------------------------------------------------------------------------
// VendorNotesPanel
// ---------------------------------------------------------------------------

export function VendorNotesPanel({ vendorName }: VendorNotesPanelProps) {
  const {
    notes,
    isLoading,
    addNote,
    isAdding,
    addNoteError,
    updateNote,
    updatingNoteId,
    deleteNote,
    deletingNoteId,
  } = useVendorNotes(vendorName);
  const [body, setBody] = useState("");
  const [composing, setComposing] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!body.trim()) return;
    try {
      await addNote(body.trim());
      setBody("");
      setComposing(false);
    } catch {
      // Keep the compose area open so the user can retry.
      // The hook's onError already fires a toast; addNoteError surfaces it inline.
    }
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold text-foreground flex items-center gap-2">
            <StickyNote className="w-4 h-4" />
            Notes
          </CardTitle>
          {!composing && (
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5 text-xs"
              onClick={() => setComposing(true)}
              title="Add note"
            >
              <Plus className="w-3.5 h-3.5" />
              Add
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Compose area */}
        {composing && (
          <form onSubmit={handleSubmit} className="space-y-2">
            <textarea
              className="w-full min-h-[72px] rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-y"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Add a note about this vendor…"
              autoFocus
            />
            {addNoteError && (
              <p role="alert" className="text-xs text-destructive">
                {addNoteError.message}
              </p>
            )}
            <div className="flex gap-2">
              <Button type="submit" size="sm" disabled={isAdding || !body.trim()}>
                {isAdding ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                ) : (
                  <Plus className="w-3.5 h-3.5 mr-1.5" />
                )}
                Save
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => {
                  setComposing(false);
                  setBody("");
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        )}

        {/* Notes timeline */}
        {isLoading ? (
          <div className="flex items-center justify-center h-16">
            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          </div>
        ) : notes.length === 0 ? (
          !composing && (
            <div className="flex flex-col items-center justify-center py-6 gap-2 text-center">
              <StickyNote className="w-10 h-10 text-muted-foreground/40" />
              <p className="text-sm text-muted-foreground">No notes yet for {vendorName}.</p>
            </div>
          )
        ) : (
          <ol className="space-y-3">
            {notes.map((note) => (
              <NoteRow
                key={note.id}
                note={note}
                onUpdate={updateNote}
                onDelete={deleteNote}
                isMutating={updatingNoteId === note.id || deletingNoteId === note.id}
              />
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}
